import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { $auditIssueFields } from '../../../../../../../../shared/constants/manual-audit-issues';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';

const issueColumns: readonly string[] = [
  $auditIssueFields.actualResult,
  $auditIssueFields.attachments,
  $auditIssueFields.codeSnippet,
  $auditIssueFields.component,
  $auditIssueFields.findingType,
  $auditIssueFields.instances,
  $auditIssueFields.issueFrequency,
  $auditIssueFields.recommendation,
  $auditIssueFields.screen,
  $auditIssueFields.stepsToReproduce,
  $auditIssueFields.summary,
  $auditIssueFields.url,
  $auditIssueFields.viewport,
  $auditIssueFields.xpath,
];

const activityColumns: readonly string[] = [
  $auditIssueFields.age,
  $auditIssueFields.createdAt,
  $auditIssueFields.status,
  $auditIssueFields.updatedAt,
  $auditIssueFields.isFlagged,
];

@Component({
  selector: 'app-issue-table-column-selector',
  templateUrl: './issue-table-column-selector.component.html',
  styleUrls: ['./issue-table-column-selector.component.scss'],
  standalone: false,
})
export class IssueTableColumnSelectorComponent implements OnChanges {
  @Input() public selectedColumns: string[];
  @Input() public excludedColumns: string[] = [];
  @Output() public selectedColumnsChange: EventEmitter<string[]>;
  @Output() public cancelled: EventEmitter<void>;

  public form: UntypedFormGroup;
  public $auditIssueFields: typeof $auditIssueFields;
  public allColumns: string[];
  public issueColumns: string[];
  public ruleInfoColumns: string[];
  public InfoColumns: readonly string[];
  public activityColumns: string[];
  public readonly maxSelected: number;
  public currentSelectedCount: number;

  constructor(private formBuilder: UntypedFormBuilder) {
    this.$auditIssueFields = $auditIssueFields;
    this.selectedColumns = [];
    this.selectedColumnsChange = new EventEmitter<string[]>();
    this.cancelled = new EventEmitter<void>();
    this.maxSelected = 10;
    this.currentSelectedCount = 0;
    this.buildSetup();
  }

  private buildSetup(): void {
    this.InfoColumns = [
      $auditIssueFields.category,
      $auditIssueFields.evaluationStandard,
      $auditIssueFields.disabilityAffected,
      $auditIssueFields.effort,
      $auditIssueFields.ruleId,
      $auditIssueFields.severity,
      $auditIssueFields.criteria,
    ];

    this.setupColumns();
    this.createForm();
  }

  private static placeFindingTypeAfterFrequency(columns: string[]): string[] {
    if (!columns.includes($auditIssueFields.findingType)) {
      return columns;
    }
    const withoutFindingType: string[] = columns.filter((column: string) => column !== $auditIssueFields.findingType);
    const frequencyIndex: number = withoutFindingType.indexOf($auditIssueFields.issueFrequency);
    if (frequencyIndex === -1) {
      return columns;
    }
    withoutFindingType.splice(frequencyIndex + 1, 0, $auditIssueFields.findingType);
    return withoutFindingType;
  }

  private setupColumns(): void {
    const effectiveExcludedColumns: string[] = this.excludedColumns || [];
    const filterExcluded = (columnName: string): boolean => !effectiveExcludedColumns.includes(columnName);
    this.activityColumns = activityColumns.filter(filterExcluded, this).sort();
    this.ruleInfoColumns = this.InfoColumns.filter(filterExcluded, this).sort();
    this.issueColumns = IssueTableColumnSelectorComponent.placeFindingTypeAfterFrequency(
      issueColumns.filter(filterExcluded, this).sort(),
    );

    this.allColumns = [...this.activityColumns, ...this.ruleInfoColumns, ...this.issueColumns];
  }

  private createForm(): void {
    this.form = this.formBuilder.group({});

    this.allColumns.forEach((column: string): void => {
      this.form.addControl(column, new FormControl<{ value: boolean }>({ value: false }));
    });
  }

  private lockControlsWhenMaxSelectedCountReached(): void {
    for (const col of this.allColumns) {
      if (this.currentSelectedCount >= this.maxSelected && this.form.get(col).value === false) {
        this.form.get(col).disable();
      } else {
        this.form.get(col).enable();
      }
    }
  }

  private unlockControlsWhenFreeSelectionAvailable(): void {
    if (this.currentSelectedCount === this.maxSelected - 1) {
      for (const col of this.allColumns) {
        this.form.get(col).enable();
      }
    }
  }

  private initializeSelectedControls(): void {
    for (const column of this.allColumns) {
      this.form.get(column).setValue(this.selectedColumns.includes(column));
    }

    this.currentSelectedCount = this.selectedColumns.length;

    this.lockControlsWhenMaxSelectedCountReached();
  }

  private getSelectedFields(): string[] {
    const selectedFields: string[] = [];
    for (const column of this.allColumns) {
      if (this.form.get(column).value) {
        selectedFields.push(column);
      }
    }
    return selectedFields;
  }

  public onSelectionChange(column: string): void {
    if (this.form.get(column).value) {
      this.currentSelectedCount += 1;
      this.lockControlsWhenMaxSelectedCountReached();
    } else {
      this.currentSelectedCount -= 1;
      this.unlockControlsWhenFreeSelectionAvailable();
    }
  }

  public onClose(): void {
    this.cancelled.emit();
  }

  public async onApply(): Promise<void> {
    this.selectedColumnsChange.emit(this.getSelectedFields());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (SharedCommonUtility.isNullish(this.selectedColumns) || SharedCommonUtility.isNullish(this.excludedColumns)) {
      return;
    }
    this.buildSetup();
    this.initializeSelectedControls();
  }
}
